import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Modal, Button, Table, Form } from 'react-bootstrap';
import Swal from 'sweetalert2';
import { API_URL } from '../../../api/api';

const API_PURCHASE_ORDER = `${API_URL}/purchase-orders`;

const PurchaseOrderLayer = () => {
  const [orders, setOrders] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({
    order_number: '',
    supplier_name: '',
    order_date: '',
    total_amount: '',
    status: 'pending',
  });
  const [editId, setEditId] = useState(null);

  const token = localStorage.getItem('token');
  if (token) {
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  }

  const fetchOrders = () => {
    axios.get(API_PURCHASE_ORDER)
      .then(res => setOrders(res.data))
      .catch(err => console.error('Error fetching purchase orders:', err));
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  const handleShowModal = (order = null) => {
    if (order) {
      setEditId(order.id);
      setForm({
        order_number: order.order_number,
        supplier_name: order.supplier_name,
        order_date: order.order_date,
        total_amount: order.total_amount,
        status: order.status,
      });
    } else {
      setEditId(null);
      setForm({
        order_number: '',
        supplier_name: '',
        order_date: '',
        total_amount: '',
        status: 'pending',
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditId(null);
    setForm({
      order_number: '',
      supplier_name: '',
      order_date: '',
      total_amount: '',
      status: 'pending',
    });
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Basic validation
    if (!form.order_number.trim() || !form.supplier_name.trim() || !form.order_date.trim() || !form.total_amount) {
      Swal.fire('Validation Error', 'Please fill all required fields', 'error');
      return;
    }

    try {
      if (editId) {
        await axios.put(`${API_PURCHASE_ORDER}/${editId}`, form);
        Swal.fire('Updated', 'Purchase Order updated successfully', 'success');
      } else {
        await axios.post(API_PURCHASE_ORDER, form);
        Swal.fire('Added', 'Purchase Order added successfully', 'success');
      }
      handleCloseModal();
      fetchOrders();
    } catch (err) {
      console.error(err);
      Swal.fire('Error', 'Operation failed', 'error');
    }
  };

  const handleDelete = async (id) => {
    const confirm = await Swal.fire({
      title: 'Are you sure?',
      text: 'This will delete the purchase order.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete it!',
    });

    if (confirm.isConfirmed) {
      try {
        await axios.delete(`${API_PURCHASE_ORDER}/${id}`);
        Swal.fire('Deleted!', 'Purchase Order has been deleted.', 'success');
        fetchOrders();
      } catch (err) {
        console.error(err);
        Swal.fire('Error', 'Delete failed', 'error');
      }
    }
  };

  return (
    <div className="row gy-4">
      <div className="col-md-12">
        <div className="card">
          <div className="card-header d-flex justify-content-between align-items-center">
            <h5 className="card-title mb-0">Purchase Order</h5>
            <Button variant="primary" size="sm" onClick={() => handleShowModal()}>
              <i className="ri-add-line" /> Add Purchase Order
            </Button>
          </div>
          <div className="card-body">
            <Table bordered hover responsive>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Order Number</th>
                  <th>Supplier Name</th>
                  <th>Order Date</th>
                  <th>Total Amount</th>
                  <th>Status</th>
                  <th style={{ width: '150px' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {orders.length > 0 ? (
                  orders.map((order, index) => (
                    <tr key={order.id}>
                      <td>{index + 1}</td>
                      <td>{order.order_number}</td>
                      <td>{order.supplier_name}</td>
                      <td>{order.order_date}</td>
                      <td>{order.total_amount}</td>
                      <td>{order.status}</td>
                      <td>
                        <Button
                          variant="warning"
                          size="sm"
                          className="me-2"
                          onClick={() => handleShowModal(order)}
                        >
                          Edit
                        </Button>
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => handleDelete(order.id)}
                        >
                          Delete
                        </Button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="7" className="text-center">
                      No purchase orders found.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </div>
      </div>

      {/* Modal */}
      <Modal show={showModal} onHide={handleCloseModal}>
        <Modal.Header closeButton>
          <h6>{editId ? 'Edit' : 'Add'} Purchase Order</h6>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>Order Number</Form.Label>
              <Form.Control
                type="text"
                name="order_number"
                value={form.order_number}
                onChange={handleChange}
                placeholder="Enter order number"
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Supplier Name</Form.Label>
              <Form.Control
                type="text"
                name="supplier_name"
                value={form.supplier_name}
                onChange={handleChange}
                placeholder="Enter supplier name"
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Order Date</Form.Label>
              <Form.Control
                type="date"
                name="order_date"
                value={form.order_date}
                onChange={handleChange}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Total Amount</Form.Label>
              <Form.Control
                type="number"
                name="total_amount"
                value={form.total_amount}
                onChange={handleChange}
                placeholder="Enter total amount"
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Status</Form.Label>
              <Form.Select
                name="status"
                value={form.status}
                onChange={handleChange}
                required
              >
                <option value="pending">Pending</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </Form.Select>
            </Form.Group>

            <div className="text-end">
              <Button variant="secondary" onClick={handleCloseModal} className="me-2">
                Cancel
              </Button>
              <Button variant="primary" type="submit">
                {editId ? 'Update' : 'Add'}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default PurchaseOrderLayer;
